/**
 * "Compartir": a temporary link to this exam's images for someone without a hub account (a colleague, a
 * second opinion). It lasts 30 minutes, 1 hour or 1 day, may ask for an access code, and can be revoked at
 * any time. Offered only on an exam opened from the hub session, to who holds `pacs:shares:write`; the
 * share routes take the user's own ris token from the hub, never the viewer ticket.
 */

import { registerCommand } from '../commands';
import type { ViewerShell } from '../shell';
import { openDialog, paragraph } from '../ui/dialogs';
import { hubRisToken, openedExam } from './hub-session';

export const SHARE_TEXT = {
  title: 'Compartir imágenes',
  intro: 'Cree un enlace temporal para que alguien sin cuenta vea las imágenes de este examen.',
  duration: 'Duración del enlace',
  durations: { 30: '30 minutos', 60: '1 hora', 1440: '1 día' } as Record<number, string>,
  useCode: 'Pedir un código de acceso',
  codeHint: 'De 4 a 12 letras o números. Entréguelo por un medio distinto al del enlace.',
  codeInvalid: 'El código debe tener de 4 a 12 letras o números.',
  create: 'Crear enlace',
  close: 'Cerrar',
  created: 'Enlace creado. Cópielo y envíelo; se muestra solo esta vez.',
  copy: 'Copiar',
  copied: 'Enlace copiado.',
  active: 'Enlaces de este examen',
  none: 'No hay enlaces activos.',
  expires: 'Vence',
  withCode: 'con código',
  opens: 'aperturas',
  revoke: 'Revocar',
  revoked: 'Enlace revocado.',
  failed: 'No fue posible completar la acción. Intente de nuevo.',
  noImages: 'Este examen aún no tiene imágenes para compartir.',
} as const;

const PERIODS = [30, 60, 1440] as const;
const CODE = /^[A-Za-z0-9]{4,12}$/;
const NOT_FOUND = 404;

interface ShareRow {
  id: string;
  expires_at: string;
  has_code: boolean;
  active: boolean;
  opens: number;
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const exam = openedExam();
  if (!exam) throw new Error('no exam');
  const response = await fetch(`${exam.risRoot.replace(/\/$/, '')}${path}`, {
    method,
    headers: { Authorization: `Bearer ${await hubRisToken()}`, ...(body ? { 'Content-Type': 'application/json' } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!response.ok) throw Object.assign(new Error(`share ${response.status}`), { status: response.status });
  return (await response.json()) as T;
}

/** The link the recipient opens: this viewer, on this site, naming the share only. */
export function shareLink(shareId: string): string {
  const url = new URL(window.location.pathname, window.location.origin);
  url.search = new URLSearchParams({ share: shareId, gateway: 'core' }).toString();
  return url.toString();
}

function when(iso: string): string {
  return new Date(iso).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' });
}

function element<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text = ''): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text) node.textContent = text;
  return node;
}

async function renderList(list: HTMLElement, examId: string, say: (text: string) => void): Promise<void> {
  list.replaceChildren(element('h3', 'share-list-title', SHARE_TEXT.active));
  let rows: ShareRow[];
  try {
    rows = (await call<{ items: ShareRow[] }>('GET', `/ris/v1/pacs/exams/${examId}/shares`)).items.filter((r) => r.active);
  } catch {
    say(SHARE_TEXT.failed);
    return;
  }
  if (rows.length === 0) list.appendChild(paragraph(list, SHARE_TEXT.none, 'share-list-empty'));
  for (const row of rows) {
    const item = element('div', 'share-row');
    const code = row.has_code ? ` · ${SHARE_TEXT.withCode}` : '';
    item.appendChild(element('span', 'share-row-text', `${SHARE_TEXT.expires} ${when(row.expires_at)}${code} · ${row.opens} ${SHARE_TEXT.opens}`));
    const revoke = element('button', 'cmd-dialog-button share-revoke', SHARE_TEXT.revoke);
    revoke.type = 'button';
    revoke.addEventListener('click', () => {
      revoke.disabled = true;
      void call('POST', `/ris/v1/pacs/exam-shares/${row.id}/revoke`)
        .then(() => {
          say(SHARE_TEXT.revoked);
          return renderList(list, examId, say);
        })
        .catch(() => {
          revoke.disabled = false;
          say(SHARE_TEXT.failed);
        });
    });
    item.appendChild(revoke);
    list.appendChild(item);
  }
}

function openShareDialog(shell: ViewerShell): void {
  const exam = openedExam();
  if (!exam) return;
  let minutes: number = PERIODS[0];
  let creating = false;
  const choices = element('div', 'share-durations');
  choices.setAttribute('role', 'radiogroup');
  choices.setAttribute('aria-label', SHARE_TEXT.duration);
  for (const period of PERIODS) {
    const label = element('label', 'share-duration');
    const radio = document.createElement('input');
    radio.type = 'radio';
    radio.name = 'share-duration';
    radio.checked = period === minutes;
    radio.addEventListener('change', () => { minutes = period; });
    label.append(radio, ` ${SHARE_TEXT.durations[period]}`);
    choices.appendChild(label);
  }
  const codeToggle = document.createElement('input');
  codeToggle.type = 'checkbox';
  const codeLabel = element('label', 'share-code-toggle');
  codeLabel.append(codeToggle, ` ${SHARE_TEXT.useCode}`);
  const code = document.createElement('input');
  code.type = 'text';
  code.className = 'share-code';
  code.maxLength = 12;
  code.autocomplete = 'off';
  code.hidden = true;
  code.setAttribute('aria-label', SHARE_TEXT.useCode);
  codeToggle.addEventListener('change', () => { code.hidden = !codeToggle.checked; });
  const result = element('div', 'share-result');
  const list = element('div', 'share-list');

  const { body } = openDialog(shell, {
    title: SHARE_TEXT.title,
    className: 'share-dialog',
    buttons: [
      { label: SHARE_TEXT.close, onClick: (close) => close() },
      {
        label: SHARE_TEXT.create,
        primary: true,
        onClick: () => {
          if (creating) return;
          const wanted = codeToggle.checked ? code.value.trim() : null;
          if (wanted !== null && !CODE.test(wanted)) return say(SHARE_TEXT.codeInvalid);
          creating = true;
          void call<{ id: string }>('POST', `/ris/v1/pacs/exams/${exam.examId}/shares`, { minutes, ...(wanted ? { code: wanted } : {}) })
            .then((made) => {
              showLink(shareLink(made.id));
              return renderList(list, exam.examId, say);
            })
            .catch((error: { status?: number }) => say(error.status === NOT_FOUND ? SHARE_TEXT.noImages : SHARE_TEXT.failed))
            .finally(() => { creating = false; });
        },
      },
    ],
  });
  const status = paragraph(body, SHARE_TEXT.intro, 'share-status');
  function say(text: string): void {
    status.textContent = text;
  }
  function showLink(link: string): void {
    const field = document.createElement('input');
    field.type = 'text';
    field.readOnly = true;
    field.value = link;
    field.className = 'share-link';
    const copy = element('button', 'cmd-dialog-button share-copy', SHARE_TEXT.copy);
    copy.type = 'button';
    copy.addEventListener('click', () => {
      field.select();
      void navigator.clipboard?.writeText(link).then(() => say(SHARE_TEXT.copied)).catch(() => undefined);
    });
    result.replaceChildren(paragraph(result, SHARE_TEXT.created, 'share-created'), field, copy);
  }
  body.append(element('h3', 'share-duration-title', SHARE_TEXT.duration), choices, codeLabel, code,
    paragraph(body, SHARE_TEXT.codeHint, 'share-code-hint'), result, list);
  void renderList(list, exam.examId, say);
}

/** Registers "Compartir" for an exam opened from the hub session (the command gate hides it otherwise). */
export function install(shell: ViewerShell): void {
  if (!openedExam()) return;
  registerCommand('share-study', () => openShareDialog(shell));
}
